import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";
import { circleIndex } from "@/theory/notes";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory14() {
  const { lesson, course, accent } = findLesson("music-theory", "14");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT14-Circle-Triads-Animated.mp4"
        caption="All 7 chords of a key fit on 7 adjacent positions"
        hint="≈ 27 sec · watch first"
      />
      <Chapter number="14" eyebrow="Music Theory · Chapter Fourteen" title="The circle as a chord chart">
        <Prose>
          <p>
            Pianists are chord people. The piano is a polyphonic instrument; we play
            three, four, five notes at a time more often than we play single lines. So
            the most useful trick the circle of fifths offers us is the one we'll build
            now: a fast, visual way to read off <em>every triad</em> in any major key —
            and the chords of the parallel minor for free.
          </p>
          <p>
            We've already seen, in earlier theory chapters, that the seven notes of a
            major scale produce three major chords, three minor chords, and one
            diminished chord. The first, fourth and fifth degrees are major; the
            second, third and sixth are minor; the seventh is diminished. That's the
            chord theory we're not re-deriving here. What we <em>are</em> doing is
            mapping those seven chords onto the circle so you can see them in clusters,
            not as a list to be remembered.
          </p>
        </Prose>
        <MTQuote>
          Major chords are grouped in a cluster: C, F, G. Minor chords are grouped in a
          cluster: D, A, E, or D, E, A, 2, 3, 6. Very easy to visualize, and the 7th happens
          to be in the lone category of diminished chords.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The major-chord cluster" title="The 1, the 4 and the 5 are always neighbours">
        <Prose>
          <p>
            Pick a root — let's use C major as the working example. Double-circle
            <strong> C</strong>. Its clockwise neighbour is <strong>G</strong> (the
            fifth) and its counter-clockwise neighbour is <strong>F</strong> (the
            fourth). Those three notes — F, C, G — are the roots of the three
            <strong> major</strong> chords in C major. They form a tight three-segment
            cluster around the root.
          </p>
          <p>
            That cluster isn't a coincidence. It's why the I, IV and V chords of any key
            feel so closely related to one another, and why so much popular music never
            strays beyond them. F major to C major and back is the <strong>plagal
            cadence</strong> — the "amen" cadence, four going to one. G major to C major
            and back is the <strong>authentic cadence</strong> — the most familiar
            ending in tonal music, five going to one. Both cadences are simply walks
            across the same three-chord neighbourhood.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The minor-chord cluster" title="The 2m, 3m and 6m sit just clockwise of the majors">
        <Prose>
          <p>
            Continue clockwise past G — the next three chord-roots you meet are
            <strong> D, A, E</strong>. Those three are the roots of the
            <strong> minor</strong> chords in C major. D minor is the ii chord, A minor
            is the vi chord (also the relative minor of C major), and E minor is the iii
            chord. Three minor chords, three contiguous segments, sitting right next to
            the three major chords.
          </p>
          <p>
            And one segment further clockwise — at the farthest point from the F-C-G
            cluster — sits <strong>B</strong>, which is the root of the lone
            <strong> diminished</strong> chord in C major (the vii° chord). B
            diminished is built from a stack of minor 3rds, and the unstable tritone
            inside it is what makes it feel so unresolved.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            rootIndex={idx("C")}
            highlights={[
              { index: idx("F"), color: "#f59e0b", label: "IV" },
              { index: idx("C"), color: "#f59e0b", label: "I" },
              { index: idx("G"), color: "#f59e0b", label: "V" },
              { index: idx("D"), color: "#a855f7", label: "ii" },
              { index: idx("A"), color: "#a855f7", label: "vi" },
              { index: idx("E"), color: "#a855f7", label: "iii" },
              { index: idx("B"), color: "#ef4444", label: "vii°" },
            ]}
          />
        </div>

        <TheoryBox label="The seven chords of C major, by colour">
          <ul className="space-y-1 text-[14px]">
            <li><span className="text-amber-300 font-semibold">Amber cluster</span> — F major (IV), C major (I), G major (V). The three majors.</li>
            <li><span className="text-purple-300 font-semibold">Purple cluster</span> — D minor (ii), A minor (vi), E minor (iii). The three minors.</li>
            <li><span className="text-rose-300 font-semibold">Rose loner</span> — B diminished (vii°). The lone diminished, sitting at the far edge.</li>
          </ul>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Cadences pop out for free" title="Authentic, plagal, deceptive — all walks on the same map">
        <Prose>
          <p>
            Once you see the three colour-coded clusters, every classical cadence
            becomes a one-step walk. The <strong>authentic cadence</strong> (V → I) is
            G to C — one step counter-clockwise across the major cluster. The
            <strong> plagal cadence</strong> (IV → I) is F to C — one step clockwise
            across the same cluster. The <strong>deceptive cadence</strong> (V → vi) is
            G to A minor — a hop from the major cluster into the minor cluster. The
            <strong> ii–V–I</strong> jazz cadence — D minor to G to C — is a walk
            counter-clockwise three segments in a row, neat as anything.
          </p>
          <p>
            Try it: pick any major key, draw the seven highlights, then trace cadences
            with your finger. You'll start seeing chord progressions as paths rather
            than as alphabetical lists. That perceptual shift is what unlocks
            improvisation and quick transposition.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Borrowed chords on the flip side" title="Walk counter-clockwise to gather the minor key">
        <Prose>
          <p>
            Here's the bonus. If you flip the qualities — make F-C-G all minor and walk
            <em> counter-clockwise</em> for the major chords instead — you get the seven
            chords of the parallel minor key. C minor's primary chords are the cluster
            <strong> F minor (iv), C minor (i), G minor (v)</strong> — same three
            roots as C major, just minor in quality. Walking counter-clockwise from C
            then gives you the three major chords of C minor: <strong>B♭ major
            (♭VII), E♭ major (♭III), A♭ major (♭VI)</strong>. Natural minor has its own lone
            diminished chord a step up from the tonic — <strong>D diminished</strong>, the ii°.
            And if you raise the 7th into harmonic minor you get the <strong>B diminished</strong>
            vii° back as well, sitting in exactly the same seat it occupied in C major.
          </p>
        </Prose>

        <TheoryBox label="One root, two whole keys">
          From a single tonic on the circle, you can read off both the major key (clockwise
          arc) and the parallel minor key (counter-clockwise arc). That's why borrowed
          chords feel so close — they live one short walk from the home triads.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Hands-on" title="Build the triads">
        <Prose>
          <p>
            The chord builder below lets you pick any root and hear all six standard
            triad qualities. Try building the I, IV and V of C major — that's C major,
            F major, G major. Then build the ii, iii and vi — D minor, E minor, A
            minor. Each chord is a stack of intervals, but the circle gave you the
            roots for free.
          </p>
        </Prose>

        <ChordBuilder root="C" families={["triad"]} />

        <Glossary>
          <Term name="Major-chord cluster" def="The three-segment arc on the circle holding the IV, I and V chords. Tight, stable, the heart of major-key music." />
          <Term name="Minor-chord cluster" def="The three-segment arc next to the major cluster, holding the ii, vi and iii chords. Where most of the melancholy and motion lives." />
          <Term name="vii° loner" def="The single diminished chord of a key, sitting at the farthest edge of the seven highlighted segments. Built on the leading tone." />
          <Term name="Authentic cadence" def="V → I. One step counter-clockwise across the major cluster. The most common ending in Western music." />
          <Term name="Plagal cadence" def="IV → I. One step clockwise across the same cluster. The 'amen' cadence." />
        </Glossary>

        <MTQuote>
          You could use the circle of fifths to form the chords of all the scales, really.
          You can even use it to form the modes, the Dorian, the Mixolydian, the Lydian and
          so on. So it's a very useful tool, the circle of fifths.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="What to practise" title="Eyes on the chord clusters">
        <Prose>
          <p>
            Choose three or four major keys you don't know well — say E major, A♭ major,
            B major, and D♭ major. For each one, draw the circle from memory, mark the
            tonic, and colour-code the seven chord segments: amber for the IV-I-V
            cluster, purple for the ii-iii-vi cluster, rose for the vii°. Then write
            the chord names next to each segment.
          </p>
          <p>
            Once you can do that for any of the twelve major keys in under a minute,
            you've internalised the chord-tree structure visually. Memorising chord
            charts becomes a non-event; you've stopped memorising and started reading.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/14a-chords-of-major-scale-on-circle.png"
          caption="Jason's note: the diatonic chord family of C major plotted on the circle of fifths. F (IV) — C (I) — G (V) sit as immediate neighbours; the ii (Dm), iii (Em), vi (Am) and vii° (B°) cluster on the other side. Major-key chord building, visualised as geography."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-15"
        label="Chapter 15 · Classification of Chords using 3 Methods"
        why="With chord-roots mapped onto the circle, the next move is to classify chords by emotion, by energy state, and by the cadences they form — three lenses for the same seven chords."
      />
    </LessonShell>
  );
}
